import {
  Button,
  FlexBox,
  JoonUIColor,
  Loader,
  SPACING,
  TextArea,
  Typography,
} from "@joonapp/web-shared"
import { MenuItem } from "@mui/material"
import { useState } from "react"

import MarkdownResultCard from "./MarkdownResultCard"
import {
  trackGenerationCompleted,
  trackGenerationFailed,
  trackGenerationStarted,
} from "./sharedTools"
import ToolPage from "./ToolPage"
import JoonSelectInput from "../../components/inputs/JoonSelectInput"
import { generateSessionSummary } from "../../networking/tools"
import { requireAuth } from "../../util/auth"

const TOOL_ID = "session_note_summarizer"

const SessionNoteSummarizer = requireAuth(() => {
  const [rawNotes, setRawNotes] = useState("")
  const [noteType, setNoteType] = useState("")
  const [templateStyle, setTemplateStyle] = useState("")
  const [summary, setSummary] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const generate = async () => {
    setLoading(true)
    setError(null)
    const t0 = Date.now()
    trackGenerationStarted(TOOL_ID, {
      note_type: noteType,
      template_style: templateStyle,
    })
    try {
      const data = await generateSessionSummary({
        rawNotes,
        noteType,
        templateStyle,
      })
      setSummary(data.summary)
      trackGenerationCompleted(TOOL_ID, { duration_ms: Date.now() - t0 })
    } catch (e: any) {
      setError("Failed to generate session summary. Please try again.")
      trackGenerationFailed(TOOL_ID, { error_code: e.message })
    }
    setLoading(false)
  }

  return (
    <ToolPage
      toolId={TOOL_ID}
      title="Session Note Summarizer"
      description="Paste raw notes or bullet points. We'll turn them into a polished SOAP, BIRP, DAP, ABC, or standard summary."
    >
      <FlexBox direction="column" gap={SPACING.space1}>
        <Typography variant="bodyBold" textAlign="left">
          Note type
        </Typography>
        <JoonSelectInput
          value={noteType}
          setValue={setNoteType}
          placeholder="Select note type"
        >
          <MenuItem value="parent">Parent session</MenuItem>
          <MenuItem value="learner">Learner session</MenuItem>
          <MenuItem value="supervision">Supervision</MenuItem>
          <MenuItem value="team_meeting">Team meeting</MenuItem>
        </JoonSelectInput>
      </FlexBox>
      <FlexBox direction="column" gap={SPACING.space1}>
        <Typography variant="bodyBold" textAlign="left">
          Template style
        </Typography>
        <JoonSelectInput
          value={templateStyle}
          setValue={setTemplateStyle}
          placeholder="Select template style"
        >
          <MenuItem value="standard">Standard</MenuItem>
          <MenuItem value="detailed">Detailed</MenuItem>
          <MenuItem value="concise">Concise</MenuItem>
          <MenuItem value="SOAP">SOAP</MenuItem>
          <MenuItem value="BIRP">BIRP</MenuItem>
          <MenuItem value="DAP">DAP</MenuItem>
          <MenuItem value="ABC">ABC</MenuItem>
        </JoonSelectInput>
      </FlexBox>
      <FlexBox direction="column" gap={SPACING.space1}>
        <Typography variant="bodyBold" textAlign="left">
          Raw session notes
        </Typography>
        <TextArea
          name="Raw session notes"
          value={rawNotes}
          onChange={(e) => setRawNotes(e.target.value)}
          placeholder="Paste raw notes or bullet points…"
          fullWidth
        />
      </FlexBox>
      <Button
        text="Generate summary"
        onClick={generate}
        disabled={loading || !rawNotes || !noteType || !templateStyle}
        fullWidth
      />
      {loading && (
        <FlexBox justify="center">
          <Loader size={24} color={JoonUIColor.semantic.primary} />
        </FlexBox>
      )}
      {error && (
        <Typography variant="bodySmall" color={JoonUIColor.semantic.alert}>
          {error}
        </Typography>
      )}
      {summary && (
        <MarkdownResultCard title="Generated summary" markdown={summary} />
      )}
    </ToolPage>
  )
})

export default SessionNoteSummarizer
